import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  PhotoIcon,
  PlayIcon,
  RectangleStackIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { projectsAPI } from '../services/api';
import { videosAPI } from '../services/videosApi';
import { getApiErrorMessage } from '../utils/apiError';
import { runProgress } from '../utils/runProgress';
import VideoUpload from '../components/VideoUpload';
import PhotoUpload from '../components/PhotoUpload';
import DeleteVideoModal from '../components/DeleteVideoModal';
// The video pages' own formatter, so a length reads the same in this list as on the video
// (QA 2026-09-29: this list's own minutes-only copy showed 80:34 for the page's 1:20:34).
import { formatDuration } from '../components/VideoHeaderStrip';
import usePublicConfig from '../hooks/usePublicConfig';
import { FEATURES } from '../config/phases';

// videos.status (app/models/video.py VideoStatus); FAILED_UPLOAD is this list's own
// terminal state for a row whose upload-file step never produced a playable video.
const STATUS_BADGES = {
  UPLOADING: { color: 'bg-blue-100 text-blue-800', label: 'Uploading' },
  READY: { color: 'bg-gray-100 text-gray-800', label: 'Ready' },
  PROCESSING: { color: 'bg-blue-100 text-blue-800', label: 'Processing' },
  DONE: { color: 'bg-green-100 text-green-800', label: 'Done' },
  FAILED: { color: 'bg-red-100 text-red-800', label: 'Failed' },
  FAILED_UPLOAD: { color: 'bg-red-100 text-red-800', label: 'Upload failed' },
};

const KIND_LABELS = { clip: 'Clip', walkthrough: 'Walkthrough', upload: 'Upload', photos: 'Photos' };

function StatusBadge({ status }) {
  const config = STATUS_BADGES[status] || { color: 'bg-gray-100 text-gray-800', label: status || 'Unknown' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
}

// Same three buckets ProjectDashboardPage.js used to render (videos.status is one of
// UPLOADING/READY/PROCESSING/DONE/FAILED/FAILED_UPLOAD): DONE folds into "ready" (the
// video is fully available either way) and UPLOADING folds into "processing" (still on
// its way to being watchable).
function videoBucket(status) {
  if (status === 'READY' || status === 'DONE') return 'ready';
  if (status === 'FAILED' || status === 'FAILED_UPLOAD') return 'failed';
  return 'processing';
}

/**
 * Slim stat strip absorbing ProjectDashboardPage's three real tiles (Videos, Runs,
 * Findings) — that page is being retired as a separate screen now that this page is the
 * project index, so its useful numbers move up here instead of disappearing.
 *
 * Every figure here is real: `videoList` is the same `project-videos` response the table
 * below renders, and `project.run_count` is the same field ProjectsPage.js already shows
 * per-project. ProjectDashboardPage's Findings tile also carried a permanent
 * "— approved · — pending" footer because no endpoint at this scope exposes per-finding
 * status — that line is dropped entirely here rather than carried forward, per instruction:
 * a fabricated or forever-dash figure is worse than no figure.
 */
function StatStrip({ project, videoList }) {
  const videoBuckets = videoList.reduce(
    (acc, video) => {
      acc[videoBucket(video.status)] += 1;
      return acc;
    },
    { ready: 0, processing: 0, failed: 0 }
  );
  const totalFindings = videoList.reduce((sum, video) => sum + (video.finding_count || 0), 0);

  return (
    <div className="mb-6 bg-white shadow-sm border border-gray-200 rounded-lg divide-y divide-gray-200 sm:divide-y-0 sm:divide-x sm:flex">
      <div className="flex-1 px-6 py-4">
        <div className="flex items-center text-gray-500">
          <RectangleStackIcon className="h-5 w-5 mr-2" />
          <span className="text-sm font-medium">Videos &amp; photos</span>
        </div>
        <div className="mt-1 flex items-baseline flex-wrap gap-x-2">
          <span className="text-2xl font-semibold text-gray-900">
            {project?.video_count ?? videoList.length}
          </span>
          <span className="text-xs text-gray-500">
            {videoBuckets.ready} ready · {videoBuckets.processing} processing
            {videoBuckets.failed > 0 ? ` · ${videoBuckets.failed} failed` : ''}
          </span>
        </div>
      </div>

      <div className="flex-1 px-6 py-4">
        <div className="flex items-center text-gray-500">
          <ChartBarIcon className="h-5 w-5 mr-2" />
          <span className="text-sm font-medium">Runs</span>
        </div>
        <div className="mt-1">
          <span className="text-2xl font-semibold text-gray-900">{project?.run_count ?? 0}</span>
        </div>
      </div>

      <div className="flex-1 px-6 py-4">
        <div className="flex items-center text-gray-500">
          <ClipboardDocumentCheckIcon className="h-5 w-5 mr-2" />
          <span className="text-sm font-medium">Findings</span>
        </div>
        <div className="mt-1">
          <span className="text-2xl font-semibold text-gray-900">{totalFindings}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * How far the GRIHA report of the video's latest run has been reviewed (RUNLOG V8): this is
 * what the retired project-level Review page used to answer, now on the row itself.
 */
function ReviewProgress({ review }) {
  if (!review) return <span className="text-gray-500">GRIHA report not written yet</span>;
  if (review.total === 0) {
    return review.writing || review.state === 'generating' ? (
      <span className="text-blue-700">Writing the GRIHA report…</span>
    ) : (
      <span className="text-red-600">GRIHA report could not be written</span>
    );
  }
  const decided = review.approved + review.rejected;
  const pct = Math.round((decided / review.total) * 100);
  return (
    <div className="min-w-[9rem]" data-testid="review-progress">
      <span className={decided === review.total ? 'text-green-700' : 'text-gray-700'}>
        {decided === review.total ? 'Reviewed' : `${decided} of ${review.total} rows reviewed`}
      </span>
      <div className="mt-1 h-1 rounded-full bg-gray-200 overflow-hidden" aria-hidden="true">
        <div className="h-full bg-green-500" style={{ width: `${pct}%` }} />
      </div>
      {review.writing && <span className="block mt-1 text-xs text-blue-700">New version being written…</span>}
    </div>
  );
}

/**
 * One video row, as its own component so it can carry its own run-status poll.
 *
 * `GET /projects/{id}/videos` (ProjectVideoSummary) has no `stage` field — only this
 * video's own lifecycle `status` — so the current pipeline stage can only come from
 * `GET /videos/{id}/status`, queried per row with its own key (`['video-status', id]`) and
 * its own polling interval, per this phase's brief.
 */
function VideoRow({ video, projectId, demoLockSeedRuns }) {
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const hasUploadedFile = video.status !== 'UPLOADING' && video.status !== 'FAILED_UPLOAD';

  const { data: runStatus } = useQuery(
    ['video-status', video.id],
    () => videosAPI.getStatus(video.id),
    {
      enabled: hasUploadedFile,
      select: (response) => response.data,
      // Only a running pipeline needs polling; an idle/done/failed run is fetched once
      // and left alone until something (Start) invalidates it.
      refetchInterval: (data) => (data?.state === 'processing' ? 3000 : false),
      retry: false,
    }
  );

  // Takes a body so the same mutation covers both buttons: Start sends nothing, Re-run sends
  // { force: true }. The route refuses a second run on a finished video without that flag
  // (7.7), which is the whole reason a re-run needs its own affordance rather than the
  // Start button simply staying visible.
  const startMutation = useMutation((body = {}) => videosAPI.process(video.id, body), {
    onSuccess: (_data, body) => {
      toast.success(body && body.force ? 'Re-run started' : 'Processing started');
      queryClient.invalidateQueries(['video-status', video.id]);
      queryClient.invalidateQueries(['project-videos', projectId]);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Could not start processing'));
    },
  });

  // D21: uploading never starts processing — Start is the auditor's own separate action,
  // and it only ever appears once a video actually has a file to run against and no run
  // is already idle-to-in-flight for it.
  const canStart = video.status === 'READY' && (!runStatus || runStatus.state === 'idle');
  const isProcessing = video.status === 'PROCESSING' || runStatus?.state === 'processing';

  // A finished run is not the end of the road.
  //
  // Until now a video whose run had ended showed no action at all: Start hides once a run
  // exists, and nothing replaced it. For a DONE video that was merely limiting - you could
  // not re-run after editing a criterion, which is exactly what QA-64 asks an auditor to do.
  // For a FAILED one it was a dead end: the row said "failed" and offered no way to try
  // again, in a demo that makes live network calls where a transient failure is entirely
  // possible. QA-42 expects an error state AND a Re-run; only the first half existed.
  const runFailed = video.status === 'FAILED' || runStatus?.state === 'failed';
  // Writing the GRIHA report holds the video's lock, so a re-run would be refused (409)
  // until it finishes: not offered meanwhile.
  const writingReport = !!video.review?.writing;
  // The demo lock (Anshul, 2026-09-26: `demo_lock_seed_runs`) 409s a re-run of the seeded
  // clip/walkthrough rows too; a control that always fails is worse than none at all
  // (2026-09-06 standing rule), so it is simply not rendered for those two kinds.
  const demoLocked = demoLockSeedRuns && (video.kind === 'clip' || video.kind === 'walkthrough');
  const canRerun =
    hasUploadedFile &&
    !isProcessing &&
    !writingReport &&
    !demoLocked &&
    (runFailed || runStatus?.state === 'done');
  // The backend refuses a delete while a run or the report writer holds the video's lock,
  // and for demo-locked seed videos (uploads.py DELETE), so Delete is not offered then.
  const canDelete = !isProcessing && !writingReport && !demoLocked;

  // A link only once the video pages exist (same FEATURES gate the route itself was added
  // under, so the two cannot drift apart), and only once there is a file behind it — a
  // video still UPLOADING has nothing to open yet.
  const canOpen = FEATURES.projectVideos && video.status !== 'UPLOADING';

  // Context-aware title link: opening a processed video to see its findings is the most
  // common action in the product, so a DONE video's title goes straight to its review
  // queue instead of forcing a tab switch nobody chose. Every other openable status
  // (READY, PROCESSING, FAILED) has no findings to review yet, so it goes to the overview.
  const titleTo =
    video.status === 'DONE' && FEATURES.projectReview
      ? `/projects/${projectId}/videos/${video.id}/review`
      : `/projects/${projectId}/videos/${video.id}`;

  // Per-row actions so a video's output is one click from the project index instead of
  // three. Each renders only when it will actually do something: the Review Queue needs a
  // finished run (it writes the GRIHA report from there if it has not been written yet), the
  // Report needs a written report, and both stay gated on the FEATURES flags their routes
  // were added under.
  const review = video.review;
  const showReview = FEATURES.projectReview && video.status === 'DONE';
  const showReport = FEATURES.projectReports && video.status === 'DONE' && review?.total > 0;

  return (
    <tr>
      <td className="px-6 py-4 max-w-xs">
        {canOpen ? (
          <Link
            to={titleTo}
            className="text-sm font-medium text-blue-600 hover:text-blue-500 truncate block"
            title={video.title}
          >
            {video.title}
          </Link>
        ) : (
          <span className="text-sm font-medium text-gray-900 truncate block" title={video.title}>
            {video.title}
          </span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {KIND_LABELS[video.kind] || video.kind}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {/* A photo set has no duration (Anshul, 2026-09-26); its own count if the list
            payload carries one, or nothing at all rather than a wrong or fabricated
            number -- ProjectVideoSummary (backend/app/schemas/project.py) does not carry
            `frame_count` today, see this phase's report. */}
        {video.kind === 'photos'
          ? typeof video.frame_count === 'number'
            ? `${video.frame_count} photo${video.frame_count === 1 ? '' : 's'}`
            : ''
          : formatDuration(video.duration_s)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <StatusBadge status={video.status} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {video.status === 'FAILED_UPLOAD' ? (
          // The upload-file step's own reason ("cannot decode video"), not just the
          // status badge's bare word: a row a caller cannot act on without knowing why is
          // as good as a dead end. There is no run behind this row (`hasUploadedFile` is
          // false for it, so the status poll is never even made), so this reads `video`,
          // not `runStatus`.
          <span className="text-red-600" title={video.upload_error || undefined}>
            {video.upload_error ? `Upload failed · ${video.upload_error}` : 'Upload failed'}
          </span>
        ) : isProcessing ? (
          `Processing · ${runProgress(runStatus, video.kind).label}`
        ) : runFailed ? (
          // The reason, not just the word "failed". "interrupted by restart" and "cannot
          // decode video" call for completely different responses from whoever is looking.
          <span className="text-red-600" title={runStatus?.error || undefined}>
            {runStatus?.error || 'Run failed'}
          </span>
        ) : video.status === 'DONE' ? (
          <ReviewProgress review={review} />
        ) : (
          '—'
        )}
      </td>
      {/* Buttons wrap to a second line rather than push past the table's edge: at 1440 px one
          unbroken line hid Re-run and Delete behind a sideways scroll (live site, 2026-09-26). */}
      <td className="px-6 py-4 text-right text-sm">
        <div className="flex flex-wrap items-center justify-end gap-2 whitespace-nowrap">
          {showReview && (
            <Link
              to={`/projects/${projectId}/videos/${video.id}/review`}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <ClipboardDocumentCheckIcon className="h-3.5 w-3.5 mr-1" />
              Review Queue
            </Link>
          )}
          {showReport && (
            <Link
              to={`/projects/${projectId}/videos/${video.id}/report`}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <DocumentTextIcon className="h-3.5 w-3.5 mr-1" />
              Report
            </Link>
          )}
          {canStart && (
            <button
              type="button"
              onClick={() => startMutation.mutate()}
              disabled={startMutation.isLoading}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              <PlayIcon className="h-3.5 w-3.5 mr-1" />
              {startMutation.isLoading ? 'Starting…' : 'Start processing'}
            </button>
          )}
          {canRerun && (
            <button
              type="button"
              onClick={() => startMutation.mutate({ force: true })}
              disabled={startMutation.isLoading}
              className={
                runFailed
                  ? 'inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50'
                  : 'inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50'
              }
              title={
                runFailed
                  ? 'Run this video again after a failure'
                  : 'Run this video again, e.g. after editing a criterion'
              }
            >
              <ArrowPathIcon className="h-3.5 w-3.5 mr-1" />
              {startMutation.isLoading ? 'Starting…' : 'Re-run'}
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center px-2 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-red-600 bg-white hover:bg-red-50"
              aria-label={`Delete ${video.title}`}
              title="Delete"
            >
              <TrashIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {confirmDelete && (
          <DeleteVideoModal video={video} projectId={projectId} onClose={() => setConfirmDelete(false)} />
        )}
      </td>
    </tr>
  );
}

export default function ProjectVideosPage() {
  const { projectId } = useParams();
  const [showUpload, setShowUpload] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const { config } = usePublicConfig();
  // `config` is `undefined` both before /config/public has answered and if it never does
  // (usePublicConfig never guesses a default); treating that as "unlocked" let Re-run and
  // Delete flash on a seeded clip/walkthrough row for the few seconds before the real
  // answer landed, then 409 when clicked. VideoRow only ever applies this to those two
  // kinds (its own `demoLocked` check below), so a plain upload is never held back by a
  // config fetch that has nothing to do with it.
  const demoLockSeedRuns = config === undefined ? true : !!config.demo_lock_seed_runs;

  // Same query key ProjectLayout.js uses for the sidebar — react-query dedupes the two
  // calls into one request since both mount in the same commit. Only feeds the stat strip;
  // a failure here degrades to the strip's own fallbacks rather than blocking the page.
  const { data: project } = useQuery(
    ['project', projectId],
    () => projectsAPI.get(projectId),
    { enabled: !!projectId, select: (response) => response.data, retry: 1 }
  );

  const { data: videoList, isLoading, isError, error, refetch } = useQuery(
    ['project-videos', projectId],
    () => projectsAPI.videos(projectId),
    {
      enabled: !!projectId,
      select: (response) => response.data || [],
      retry: 1,
      // A row whose run or report is still being written reads again until it is done, so
      // its Progress column and its Review Queue / Report links appear without a reload.
      // UPLOADING too (Anshul, 2026-09-26): a large direct upload -- video or photo set --
      // finalizes in the background after the upload modal itself has already closed, so
      // nothing else would ever tell this list the row became READY.
      refetchInterval: (list) =>
        (list || []).some((v) => v.status === 'PROCESSING' || v.status === 'UPLOADING' || v.review?.writing)
          ? 5000
          : false,
    }
  );

  const videos = videoList || [];

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="max-w-8xl mx-auto animate-pulse" data-testid="videos-loading">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6" />
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <div className="max-w-8xl mx-auto text-center py-12">
          <RectangleStackIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading videos &amp; photos</h3>
          <p className="mt-1 text-sm text-gray-500">{getApiErrorMessage(error, 'Something went wrong')}</p>
          <div className="mt-6">
            <button
              onClick={() => refetch()}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-8xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Videos &amp; photos</h1>
            <p className="mt-1 text-sm text-gray-500">
              Upload walkthrough videos or site photos and run them against this project's standard
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPhotoUpload(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-[#F8F8FF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PhotoIcon className="h-4 w-4 mr-2" />
              Upload photos
            </button>
            <button
              onClick={() => setShowUpload(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
              Upload video
            </button>
          </div>
        </div>

        {videos.length > 0 && <StatStrip project={project} videoList={videos} />}

        {videos.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <RectangleStackIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No videos or photos yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Upload a walkthrough video, or a set of photos, to start a compliance run against this project's standard.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2">
              <button
                onClick={() => setShowPhotoUpload(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-[#F8F8FF]"
              >
                <PhotoIcon className="h-4 w-4 mr-2" />
                Upload photos
              </button>
              <button
                onClick={() => setShowUpload(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                Upload your first video
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
            {/* `relative` is load-bearing: the sr-only "Actions" header below is
                position:absolute, and an absolutely positioned box escapes a scroll container
                that is not itself positioned - at 150 % zoom it sat at x=1155 and widened the
                whole page by 302 px (QA-47, found the same day the header was added). */}
            <div className="relative overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-[#F8F8FF]">
                  {/* "Name" and "Length", not "Video" and "Duration": a row may be a photo set,
                      whose length is a photo count (QA 2026-09-29). */}
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kind
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Length
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progress
                    </th>
                    {/* QA-46 (axe td-has-header, critical): an empty <th> leaves every
                        Review/Report/Start cell with no header to be read against. */}
                    <th className="px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {videos.map((video) => (
                    <VideoRow
                      key={video.id}
                      video={video}
                      projectId={projectId}
                      demoLockSeedRuns={demoLockSeedRuns}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <VideoUpload isOpen={showUpload} projectId={projectId} onClose={() => setShowUpload(false)} />
      <PhotoUpload isOpen={showPhotoUpload} projectId={projectId} onClose={() => setShowPhotoUpload(false)} />
    </div>
  );
}
